import React, {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useRef,
  RefObject,
} from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { useNavigationConfirmation } from 'hooks/useNavigationConfirmation';

export interface AssessmentsContextValue {
  formMethods: UseFormReturn<FieldValues>;
  /** Map: questionId → DOM element */
  questionRefs: MutableRefObject<Map<number, HTMLElement>>;
  /** Cached ref callbacks to ensure stability across renders */
  refCallbacks: MutableRefObject<Map<number, (el: HTMLElement | null) => void>>;
  /** Pending scroll target after page change */
  pendingScrollTarget: MutableRefObject<number | null>;
  /** Ref to the scrollable assessments container DOM element */
  assessmentsFormRef: RefObject<HTMLFormElement>;
  /** Called after the final submit has been saved, when assessments run inside the checklist */
  onSubmitSuccess?: () => void;
}

export const AssessmentsContext = createContext<AssessmentsContextValue | null>(null);

interface AssessmentsProviderProps {
  onSubmitSuccess?: () => void;
}

export const AssessmentsProvider: React.FC<PropsWithChildren<AssessmentsProviderProps>> = ({
  children,
  onSubmitSuccess,
}) => {
  const formMethods = useForm();

  // Map: questionId → DOM element
  const questionRefs = useRef<Map<number, HTMLElement>>(new Map());

  // Ref to the scrollable assessments container DOM element
  const assessmentsFormRef = useRef<HTMLFormElement>(null);

  // Cached ref callbacks to ensure stability across renders
  const refCallbacks = useRef<Map<number, (el: HTMLElement | null) => void>>(new Map());

  // Pending scroll target after page change
  const pendingScrollTarget = useRef<number | null>(null);

  const hasUnsavedChanges = formMethods.formState.isDirty;
  useNavigationConfirmation(hasUnsavedChanges);

  return (
    <AssessmentsContext.Provider
      value={{
        formMethods,
        questionRefs,
        refCallbacks,
        pendingScrollTarget,
        assessmentsFormRef,
        onSubmitSuccess,
      }}
    >
      {children}
    </AssessmentsContext.Provider>
  );
};
